/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import noop from 'lodash/noop';
import { useRouter } from 'next/navigation';
import {
  type ComponentPropsWithoutRef,
  type FocusEvent,
  type MouseEventHandler,
  useRef,
} from 'react';
import { useFocusWithin } from 'react-aria';
import type { MessageKey } from 'types/LocaleMessageKey';

import BackdropBlur from '@/app/components/BackdropBlur';
import type { ImageProps } from '@/app/components/Image';
import Image from '@/app/components/Image';
import type { MotionProps } from '@/app/components/Motion';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';

import LandingPageHeroChicletCtaLink, {
  type LandingPageHeroChicletCtaLinkProps,
} from './LandingPageHeroChicletCtaLink';
import LandingPageHeroChicletModal, {
  type LandingPageHeroChicletModalProps,
} from './LandingPageHeroChicletModal';

export interface LandingPageHeroChicletProps
  extends ComponentPropsWithoutRef<'div'> {
  readonly cta?: LandingPageHeroChicletCtaLinkProps | undefined;
  readonly image: Pick<
    ImageProps,
    'alt' | 'format' | 'localizedAlt' | 'localizedSrc' | 'src'
  >;
  readonly modal?:
    | Omit<LandingPageHeroChicletModalProps, 'buttonRef'>
    | undefined;
  readonly motionProps?: MotionProps;
  readonly onFocusWithin?: (e: FocusEvent) => void;
  readonly title: MessageKey;
}

const LandingPageHeroChiclet = ({
  cta,
  image,
  modal,
  motionProps = {},
  onClick,
  onFocusWithin = noop,
  title,
  ...props
}: LandingPageHeroChicletProps) => {
  const router = useRouter();
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin,
  });
  const modalButtonRef = useRef<HTMLButtonElement>(null);
  const _motionProps: MotionProps = {
    ...motionProps,
    animate: { opacity: 1 },
    initial: { opacity: 0 },
    transition: {
      duration: 1,
      ...motionProps.transition,
      type: 'just',
    },
  };
  const handleWrapperClick: MouseEventHandler<HTMLDivElement> = (e) => {
    onClick?.(e);
    if (modal) {
      modalButtonRef.current?.click();
    }
    if (cta) {
      router.push(cta.href);
    }
  };
  return (
    <div
      data-cy="landing-page-hero-chiclet"
      {...focusWithinProps}
      {...props}
      className={cx(
        'mb-6 inline-block w-full max-w-[360px] cursor-pointer max-md:mx-auto',
        props.className,
      )}
      onClick={handleWrapperClick}
    >
      <div className="relative z-10 p-1">
        <BackdropBlur
          {..._motionProps}
          color={{ sm: 'dark/40', lg: 'dark/40' }}
          variant="chiclet"
        />
        <Motion className="relative flex" {..._motionProps}>
          <div className="relative aspect-3/2 w-44 overflow-hidden rounded-xl px-20">
            <Image className="object-cover" fill sizes="200px" {...image} />
          </div>
          <div className="flex flex-col justify-center px-4 pt-3 pb-4 lg:pr-7 lg:pl-6">
            <TypographyV3
              className="mb-3 leading-snug"
              color="light"
              variant="body-small"
            >
              <RichText messageKey={title} />
            </TypographyV3>
            {cta && <LandingPageHeroChicletCtaLink {...cta} />}
            {modal && (
              <LandingPageHeroChicletModal
                buttonRef={modalButtonRef}
                {...modal}
              />
            )}
          </div>
        </Motion>
      </div>
    </div>
  );
};

export default LandingPageHeroChiclet;
